import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { saveAs } from "file-saver";
import PdfViewer from "../../PdfViewer";
import { extraerDatosSiteSurvey } from "./siteSurveyExtractor";
import { generarDocumentoBOM } from "./bomGenerator";
import RevisionOrtografica from "./RevisionOrtografica";
import CalendarioProyecto from "./CalendarioProyecto";
import {
  generarSlugProyecto,
  guardarMetadataProyecto,
  listarProyectos,
  subirArchivoPaso,
  eliminarProyectoS3,
} from "./s3Cliente";
import "../../incidentes.css";
import "bootstrap/dist/css/bootstrap.min.css";

const PASO_SITE_SURVEY = 1;
const PASO_BOM = 2;
const DIAS_LIMITE_PROYECTO_DEFAULT = 7;
// A partir de este paso (Agreement) ya no se puede regresar: los pasos
// posteriores (SOW, Kick-off) son definitivos y no admiten regenerar
// documentos anteriores como el BOM o las propuestas.
const PASO_SIN_RETROCESO = 5;
// Solo estos pasos admiten "realizar un cambio" (nueva versión) sobre un
// documento ya creado; a partir de Agreement el proceso es definitivo.
const PASO_MAX_VERSIONABLE = 4;

const pasos = [
  {
    numero: 1,
    titulo: "Site Survey",
    entrada: "Documento de Site Survey del proyecto (mediciones, AP, cobertura, planos).",
    entradaAccept: ".pdf,.doc,.docx,.xls,.xlsx",
    salida: "Site Survey validado",
    formatoSalida: "PDF",
  },
  {
    numero: 2,
    titulo: "BOM",
    entrada: "Site Survey validado del paso anterior.",
    entradaAccept: ".pdf,.doc,.docx,.xls,.xlsx",
    salida: "Bill of Materials (BOM)",
    formatoSalida: "Excel / PDF",
  },
  {
    numero: 3,
    titulo: "Propuesta técnico-funcional",
    entrada: "BOM aprobado del paso anterior.",
    entradaAccept: ".pdf,.doc,.docx,.xls,.xlsx",
    salida: "Propuesta técnico-funcional",
    formatoSalida: "Word / PDF",
  },
  {
    numero: 4,
    titulo: "Propuesta comercial",
    entrada: "Propuesta técnico-funcional del paso anterior.",
    entradaAccept: ".pdf,.doc,.docx",
    salida: "Propuesta comercial",
    formatoSalida: "Word / PDF",
  },
  {
    numero: 5,
    titulo: "Agreement",
    entrada: "Propuesta comercial aprobada por el cliente.",
    entradaAccept: ".pdf,.doc,.docx",
    salida: "Agreement",
    formatoSalida: "Word / PDF",
  },
  {
    numero: 6,
    titulo: "SOW",
    entrada: "Agreement firmado por el cliente.",
    entradaAccept: ".pdf,.doc,.docx",
    salida: "Statement of Work (SOW)",
    formatoSalida: "Word / PDF",
  },
  {
    numero: 7,
    titulo: "Kick-off",
    entrada: "SOW firmado por el cliente.",
    entradaAccept: ".pdf,.doc,.docx",
    salida: "Acta de Kick-off",
    formatoSalida: "Word / PDF",
  },
];

const EMPRESAS = [
  { id: "Netjer", nombre: "Netjer", logo: `${process.env.PUBLIC_URL || ""}/img/NJ-Logotipo.png` },
  { id: "Rexten", nombre: "Rexten", logo: `${process.env.PUBLIC_URL || ""}/img/rexten.png` },
];

const MESES_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const formatearFechaLarga = (fecha) =>
  `${fecha.getDate()} de ${MESES_ES[fecha.getMonth()]} de ${fecha.getFullYear()}`;

const formatearTamanio = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const unidades = ["B", "KB", "MB", "GB"];
  let valor = bytes;
  let i = 0;
  while (valor >= 1024 && i < unidades.length - 1) {
    valor /= 1024;
    i += 1;
  }
  return `${valor.toFixed(1)} ${unidades[i]}`;
};

const CreacionDocumentacion = () => {
  const [vista, setVista] = useState("inicio");
  const [proyectos, setProyectos] = useState([]);
  const [proyectoActivoId, setProyectoActivoId] = useState(null);
  const [nombreNuevoProyecto, setNombreNuevoProyecto] = useState("");
  const [empresaNuevoProyecto, setEmpresaNuevoProyecto] = useState(EMPRESAS[0].id);
  const [diasLimiteNuevoProyecto, setDiasLimiteNuevoProyecto] = useState(
    DIAS_LIMITE_PROYECTO_DEFAULT
  );
  const [creandoProyecto, setCreandoProyecto] = useState(false);
  const [cargandoProyectos, setCargandoProyectos] = useState(true);
  const [errorProyectos, setErrorProyectos] = useState("");

  const [pasoActivo, setPasoActivo] = useState(1);
  const [archivosPorPaso, setArchivosPorPaso] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generandoBOM, setGenerandoBOM] = useState(false);
  const [bomBlob, setBomBlob] = useState(null);
  const [errorBOM, setErrorBOM] = useState("");
  const [datosSiteSurvey, setDatosSiteSurvey] = useState(null);
  const [revisionAbierta, setRevisionAbierta] = useState(false);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);
  const [errorSubida, setErrorSubida] = useState("");
  const [cambiandoVersion, setCambiandoVersion] = useState(false);

  const archivo = archivosPorPaso[pasoActivo] || null;

  useEffect(() => {
    let cancelado = false;
    setCargandoProyectos(true);
    setErrorProyectos("");
    listarProyectos()
      .then((lista) => {
        if (cancelado) return;
        setProyectos(
          lista.map((p) => ({ ...p, fechaCreacion: new Date(p.fechaCreacion) }))
        );
      })
      .catch((error) => {
        console.error(error);
        if (!cancelado) {
          setErrorProyectos(
            "No se pudieron cargar los proyectos guardados en S3. Intenta recargar la página."
          );
        }
      })
      .finally(() => {
        if (!cancelado) setCargandoProyectos(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!archivo) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(archivo);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [archivo]);

  const handleArchivoChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    setArchivosPorPaso((prev) => ({ ...prev, [pasoActivo]: file || null }));
    if (pasoActivo === PASO_SITE_SURVEY) {
      setBomBlob(null);
      setErrorBOM("");
    }
    setErrorSubida("");

    if (!file || !proyectoActivo) return;

    setSubiendoArchivo(true);
    try {
      await subirArchivoPaso({
        proyectoId: proyectoActivo.id,
        pasoNumero: pasoActivo,
        tituloPaso: pasoActual.titulo,
        version: (proyectoActivo.versionesPorPaso && proyectoActivo.versionesPorPaso[pasoActivo]) || 1,
        tipo: "cargado",
        archivo: file,
      });
    } catch (error) {
      console.error("No se pudo guardar el archivo cargado en S3.", error);
      setErrorSubida(
        "No se pudo guardar el archivo en el histórico de S3 (se sigue usando localmente)."
      );
    } finally {
      setSubiendoArchivo(false);
    }
  };

  const registrarProgresoProyecto = (numero) => {
    if (!proyectoActivo) return;
    const actualizado = {
      ...proyectoActivo,
      pasoMaximoAlcanzado: Math.max(proyectoActivo.pasoMaximoAlcanzado || 1, numero),
    };
    setProyectos((prev) => prev.map((p) => (p.id === actualizado.id ? actualizado : p)));
    guardarMetadataProyecto(actualizado).catch((error) =>
      console.error("No se pudo guardar el progreso del proyecto en S3.", error)
    );
  };

  const realizarCambioVersion = async () => {
    if (!proyectoActivo) return;
    const versionActual =
      (proyectoActivo.versionesPorPaso && proyectoActivo.versionesPorPaso[pasoActivo]) || 1;
    const actualizado = {
      ...proyectoActivo,
      versionesPorPaso: {
        ...(proyectoActivo.versionesPorPaso || {}),
        [pasoActivo]: versionActual + 1,
      },
    };

    setCambiandoVersion(true);
    setErrorSubida("");
    try {
      await guardarMetadataProyecto(actualizado);
      setProyectos((prev) => prev.map((p) => (p.id === actualizado.id ? actualizado : p)));
      setArchivosPorPaso((prev) => ({ ...prev, [pasoActivo]: null }));
      if (pasoActivo === PASO_BOM) {
        setBomBlob(null);
        setErrorBOM("");
      }
    } catch (error) {
      console.error("No se pudo iniciar una nueva versión.", error);
      setErrorSubida("No se pudo iniciar una nueva versión. Intenta de nuevo.");
    } finally {
      setCambiandoVersion(false);
    }
  };

  const irAPaso = (numero) => {
    if (pasoActivo > PASO_SIN_RETROCESO && numero < pasoActivo) return;
    setPasoActivo(numero);
    registrarProgresoProyecto(numero);
  };

  const irAPasoAnterior = () => {
    if (pasoActivo > PASO_SIN_RETROCESO) return;
    setPasoActivo((actual) => Math.max(1, actual - 1));
  };

  const irAPasoSiguiente = () => {
    const siguiente = Math.min(pasos.length, pasoActivo + 1);
    setPasoActivo(siguiente);
    registrarProgresoProyecto(siguiente);
  };

  const avanzarPaso = async () => {
    const archivoSiteSurvey = archivosPorPaso[PASO_SITE_SURVEY];
    const debeGenerarBOM =
      pasoActivo === PASO_SITE_SURVEY && archivoSiteSurvey && !bomBlob;

    if (!debeGenerarBOM) {
      irAPasoSiguiente();
      return;
    }

    setGenerandoBOM(true);
    setErrorBOM("");
    try {
      const esDocx = /\.docx?$/i.test(archivoSiteSurvey.name);
      if (!esDocx) {
        throw new Error("El Site Survey debe ser un documento de Word (.docx) para heredar sus datos.");
      }
      const datos = await extraerDatosSiteSurvey(archivoSiteSurvey);
      setDatosSiteSurvey(datos);
      setRevisionAbierta(true);
    } catch (error) {
      console.error(error);
      setErrorBOM(
        error.message || "No se pudo generar el BOM a partir del Site Survey."
      );
      setGenerandoBOM(false);
    }
  };

  const confirmarRevisionOrtografica = async (datosCorregidos) => {
    setRevisionAbierta(false);
    try {
      const { logoCliente, nombreProyecto, ...secciones } = datosCorregidos;
      const nombreFinal = nombreProyecto || (proyectoActivo ? proyectoActivo.nombre : "Proyecto");
      const blob = await generarDocumentoBOM({
        nombreProyecto: nombreFinal,
        fechaCreacion: formatearFechaLarga(new Date()),
        logoCliente,
        empresa: proyectoActivo ? proyectoActivo.empresa : EMPRESAS[0].id,
        secciones,
      });
      setBomBlob(blob);

      if (proyectoActivo) {
        try {
          await subirArchivoPaso({
            proyectoId: proyectoActivo.id,
            pasoNumero: PASO_BOM,
            tituloPaso: pasos.find((p) => p.numero === PASO_BOM).titulo,
            version:
              (proyectoActivo.versionesPorPaso && proyectoActivo.versionesPorPaso[PASO_BOM]) || 1,
            tipo: "generado",
            archivo: blob,
            nombreArchivo: `BOM_${nombreFinal.replace(/\s+/g, "_")}.docx`,
          });
        } catch (errorSubidaBOM) {
          console.error("No se pudo guardar el BOM generado en S3.", errorSubidaBOM);
        }
      }
    } catch (error) {
      console.error(error);
      setErrorBOM(
        error.message || "No se pudo generar el BOM a partir del Site Survey."
      );
    } finally {
      setGenerandoBOM(false);
      setDatosSiteSurvey(null);
      irAPasoSiguiente();
    }
  };

  const cancelarRevisionOrtografica = () => {
    setRevisionAbierta(false);
    setDatosSiteSurvey(null);
    setGenerandoBOM(false);
  };

  const descargarBOM = () => {
    if (!bomBlob) return;
    const nombreArchivo = `BOM_${(proyectoActivo ? proyectoActivo.nombre : "Proyecto").replace(/\s+/g, "_")}.docx`;
    saveAs(bomBlob, nombreArchivo);
  };

  const iniciarCreacionProyecto = () => {
    setNombreNuevoProyecto(`Proyecto ${proyectos.length + 1}`);
    setEmpresaNuevoProyecto(EMPRESAS[0].id);
    setDiasLimiteNuevoProyecto(DIAS_LIMITE_PROYECTO_DEFAULT);
    setErrorProyectos("");
    setVista("nombrando");
  };

  const confirmarCreacionProyecto = async (event) => {
    event.preventDefault();
    const nombre = nombreNuevoProyecto.trim() || "Proyecto sin nombre";
    const duracionDias = Math.max(1, Number(diasLimiteNuevoProyecto) || DIAS_LIMITE_PROYECTO_DEFAULT);
    const nuevoProyecto = {
      id: generarSlugProyecto(nombre),
      nombre,
      empresa: empresaNuevoProyecto,
      fechaCreacion: new Date(),
      duracionDias,
      pasoMaximoAlcanzado: 1,
      versionesPorPaso: Object.fromEntries(pasos.map((p) => [p.numero, 1])),
    };

    setCreandoProyecto(true);
    setErrorProyectos("");
    try {
      await guardarMetadataProyecto(nuevoProyecto);
      setProyectos((prev) => [nuevoProyecto, ...prev]);
      setProyectoActivoId(nuevoProyecto.id);
      setNombreNuevoProyecto("");
      setPasoActivo(1);
      setArchivosPorPaso({});
      setBomBlob(null);
      setErrorBOM("");
      setVista("proyecto");
    } catch (error) {
      console.error("No se pudo guardar el proyecto nuevo en S3.", error);
      setErrorProyectos("No se pudo guardar el proyecto nuevo en S3. Intenta de nuevo.");
    } finally {
      setCreandoProyecto(false);
    }
  };

  const cancelarCreacionProyecto = () => {
    setNombreNuevoProyecto("");
    setVista(proyectos.length > 0 ? "proyecto" : "inicio");
  };

  const seleccionarProyecto = (id) => {
    const proyecto = proyectos.find((p) => p.id === id);
    setProyectoActivoId(id);
    // Retoma en el paso más avanzado ya alcanzado: si el proyecto ya
    // superó el paso sin retroceso, no debe poder "reaparecer" en pasos
    // anteriores solo por salir y volver a entrar al proyecto.
    setPasoActivo(proyecto ? proyecto.pasoMaximoAlcanzado || 1 : 1);
    setArchivosPorPaso({});
    setBomBlob(null);
    setErrorBOM("");
    setVista("proyecto");
  };

  const eliminarProyecto = async (id, event) => {
    event.stopPropagation();
    const proyecto = proyectos.find((p) => p.id === id);
    if (!proyecto) return;

    const confirmado = window.confirm(
      `¿Eliminar el proyecto "${proyecto.nombre}"? Esta acción no se puede deshacer y borrará todos sus archivos guardados en S3.`
    );
    if (!confirmado) return;

    const restantes = proyectos.filter((p) => p.id !== id);
    setProyectos(restantes);

    if (id === proyectoActivoId) {
      if (restantes.length > 0) {
        setProyectoActivoId(restantes[0].id);
        setPasoActivo(restantes[0].pasoMaximoAlcanzado || 1);
        setArchivosPorPaso({});
        setBomBlob(null);
        setErrorBOM("");
        setVista("proyecto");
      } else {
        setProyectoActivoId(null);
        setVista("inicio");
      }
    }

    try {
      await eliminarProyectoS3(id);
    } catch (error) {
      console.error("No se pudo eliminar el proyecto de S3.", error);
    }
  };

  const proyectoActivo = proyectos.find((p) => p.id === proyectoActivoId);
  const pasoActual = pasos.find((p) => p.numero === pasoActivo) || pasos[0];
  const esImagen = archivo && archivo.type.startsWith("image/");
  const esPdf = archivo && archivo.type === "application/pdf";

  return (
    <>
      <Container fluid className="background-containerr">
        {vista === "inicio" && (
          <div className="orgproc-header">
            <h1 className="orgproc-heading">Creación de Documentación</h1>
          </div>
        )}

        {vista === "inicio" && (
          <div className="docgen-landing">
            {cargandoProyectos && (
              <p className="docgen-landing-estado">Cargando proyectos guardados...</p>
            )}

            {!cargandoProyectos && errorProyectos && (
              <p className="docgen-landing-estado docgen-landing-estado--error">
                {errorProyectos}
              </p>
            )}

            <button
              type="button"
              className="docgen-landing-btn"
              onClick={iniciarCreacionProyecto}
            >
              <span className="docgen-landing-icon">+</span>
              Crear nuevo proyecto
            </button>

            {!cargandoProyectos && proyectos.length > 0 && (
              <div className="docgen-landing-lista">
                <div className="docgen-landing-lista-titulo">
                  Proyectos guardados ({proyectos.length})
                </div>
                <div className="docgen-landing-grid">
                  {proyectos.map((proyecto) => (
                    <button
                      key={proyecto.id}
                      type="button"
                      className="docgen-landing-card"
                      onClick={() => seleccionarProyecto(proyecto.id)}
                    >
                      <span className="docgen-landing-card-icono">📁</span>
                      <span className="docgen-landing-card-nombre">
                        {proyecto.nombre}
                      </span>
                      <span className="docgen-landing-card-paso">
                        Paso {proyecto.pasoMaximoAlcanzado || 1} de {pasos.length}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {(vista === "proyecto" || vista === "nombrando") && (
          <div className="docgen-layout">
            <aside className="docgen-sidebar">
              <div className="docgen-sidebar-header">Proyectos</div>
              <button
                type="button"
                className="docgen-sidebar-new-btn"
                onClick={iniciarCreacionProyecto}
              >
                <span className="docgen-sidebar-new-icon">+</span>
                Nuevo proyecto
              </button>

              <div className="docgen-folder-list">
                {proyectos.map((proyecto) => (
                  <div
                    key={proyecto.id}
                    className={
                      "docgen-folder-item" +
                      (proyecto.id === proyectoActivoId && vista === "proyecto"
                        ? " docgen-folder-item--active"
                        : "")
                    }
                  >
                    <button
                      type="button"
                      className="docgen-folder-select"
                      onClick={() => seleccionarProyecto(proyecto.id)}
                    >
                      <span className="docgen-folder-icon">📁</span>
                      <span className="docgen-folder-name">
                        {proyecto.nombre}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="docgen-folder-delete"
                      onClick={(event) => eliminarProyecto(proyecto.id, event)}
                      aria-label={`Eliminar ${proyecto.nombre}`}
                      title="Eliminar proyecto"
                    >
                      🗑
                    </button>
                  </div>
                ))}
              </div>
            </aside>

            {vista === "nombrando" && (
              <div className="docgen-main">
                <div className="orgproc-header">
                  <h1 className="orgproc-heading">Creación de Documentación</h1>
                </div>

                <div className="docgen-grid">
                  <div className="orgproc-card">
                    <div className="orgproc-card-accent"></div>
                    <div className="orgproc-card-body">
                      <div className="orgproc-eyebrow">Nuevo proyecto</div>
                      <div className="orgproc-card-title">
                        Nombre del proyecto
                      </div>
                      <p className="orgproc-card-desc">
                        Este nombre identificará al proyecto en toda la
                        documentación generada y no podrá modificarse una
                        vez creado.
                      </p>
                      <form onSubmit={confirmarCreacionProyecto}>
                        <input
                          type="text"
                          className="docgen-name-input"
                          value={nombreNuevoProyecto}
                          onChange={(event) =>
                            setNombreNuevoProyecto(event.target.value)
                          }
                          autoFocus
                          maxLength={80}
                        />

                        <div className="docgen-empresa-label">Empresa</div>
                        <p className="orgproc-card-desc">
                          Define qué logo llevará el encabezado del BOM y
                          demás documentación generada para este proyecto.
                        </p>
                        <div className="docgen-empresa-selector" role="radiogroup" aria-label="Empresa">
                          {EMPRESAS.map((empresa) => (
                            <button
                              key={empresa.id}
                              type="button"
                              role="radio"
                              aria-checked={empresaNuevoProyecto === empresa.id}
                              className={
                                "docgen-empresa-option" +
                                (empresaNuevoProyecto === empresa.id
                                  ? " docgen-empresa-option--activa"
                                  : "")
                              }
                              onClick={() => setEmpresaNuevoProyecto(empresa.id)}
                            >
                              <img
                                src={empresa.logo}
                                alt={`Logo ${empresa.nombre}`}
                                className="docgen-empresa-logo"
                              />
                              <span className="docgen-empresa-nombre">{empresa.nombre}</span>
                            </button>
                          ))}
                        </div>

                        <div className="docgen-empresa-label">Tiempo límite</div>
                        <p className="orgproc-card-desc">
                          Días disponibles para completar todo el proyecto
                          (los 6 pasos). Se reparte en partes iguales entre
                          cada paso para calcular sus fechas límite.
                        </p>
                        <input
                          type="number"
                          min={1}
                          className="docgen-name-input"
                          value={diasLimiteNuevoProyecto}
                          onChange={(event) =>
                            setDiasLimiteNuevoProyecto(event.target.value)
                          }
                        />

                        {errorProyectos && (
                          <p className="docgen-output-hint docgen-output-hint--error">
                            {errorProyectos}
                          </p>
                        )}

                        <div className="docgen-card-actions">
                          <button
                            type="submit"
                            className="orgproc-card-btn"
                            disabled={!nombreNuevoProyecto.trim() || creandoProyecto}
                          >
                            {creandoProyecto ? "Guardando..." : "Crear proyecto →"}
                          </button>
                          {proyectos.length > 0 && (
                            <button
                              type="button"
                              className="docgen-cancel-btn"
                              onClick={cancelarCreacionProyecto}
                              disabled={creandoProyecto}
                            >
                              Cancelar
                            </button>
                          )}
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {vista === "proyecto" && (
            <div className="docgen-main">
              {proyectoActivo && (
                <div className="orgproc-header">
                  <h1 className="orgproc-heading">{proyectoActivo.nombre}</h1>
                </div>
              )}

              <div className="docgen-steps">
                {pasos.map((paso, index) => {
                  const esCompletado = paso.numero < pasoActivo;
                  const esActivo = paso.numero === pasoActivo;
                  const bloqueado =
                    pasoActivo > PASO_SIN_RETROCESO && paso.numero < pasoActivo;
                  return (
                  <React.Fragment key={paso.numero}>
                    <button
                      type="button"
                      className={
                        "docgen-step" +
                        (esActivo ? " docgen-step--activo" : "") +
                        (bloqueado ? " docgen-step--bloqueado" : "")
                      }
                      onClick={() => irAPaso(paso.numero)}
                      disabled={bloqueado}
                      title={
                        bloqueado
                          ? "Ya no se puede regresar a este paso después de Agreement."
                          : undefined
                      }
                    >
                      <div
                        className={
                          "docgen-step-circle" +
                          (esCompletado
                            ? " docgen-step-circle--completado"
                            : esActivo
                            ? " docgen-step-circle--activo"
                            : "")
                        }
                      >
                        {paso.numero}
                      </div>
                      <div className="docgen-step-label">{paso.titulo}</div>
                    </button>
                    {index < pasos.length - 1 && (
                      <div className="docgen-connector" aria-hidden="true">
                        →
                      </div>
                    )}
                  </React.Fragment>
                  );
                })}
              </div>

              <div className="docgen-grid">
                <div className="orgproc-card">
                  <div className="orgproc-card-accent"></div>
                  <div className="orgproc-card-body">
                    <div className="orgproc-eyebrow">
                      Paso {pasoActual.numero} de {pasos.length}
                      <span className="docgen-version-badge">
                        v{(proyectoActivo?.versionesPorPaso?.[pasoActivo]) || 1}
                      </span>
                    </div>
                    <div className="orgproc-card-title">{pasoActual.titulo}</div>

                    {pasoActivo > PASO_SIN_RETROCESO && (
                      <div className="docgen-alerta docgen-alerta--proximo">
                        🔒 A partir de Agreement el proceso es definitivo: ya no
                        se puede regresar a pasos anteriores.
                      </div>
                    )}

                    <p className="orgproc-card-desc">
                      Ingresa el siguiente documento: {pasoActual.entrada} A
                      partir de este documento se generará la documentación de
                      este paso usando el machote correspondiente.
                    </p>
                    <label
                      className="docgen-upload-dropzone"
                      htmlFor={`paso-${pasoActual.numero}-input`}
                    >
                      <input
                        key={pasoActual.numero}
                        id={`paso-${pasoActual.numero}-input`}
                        type="file"
                        className="docgen-upload-input"
                        accept={pasoActual.entradaAccept}
                        onChange={handleArchivoChange}
                      />
                      <span className="docgen-upload-icon">⬆</span>
                      <span className="docgen-upload-text">
                        {archivo
                          ? archivo.name
                          : `Selecciona o arrastra el documento de ${pasoActual.titulo}`}
                      </span>
                    </label>

                    {subiendoArchivo && (
                      <p className="docgen-output-hint">Guardando en el histórico de S3...</p>
                    )}
                    {!subiendoArchivo && errorSubida && (
                      <p className="docgen-output-hint docgen-output-hint--error">
                        {errorSubida}
                      </p>
                    )}

                    {pasoActual.numero <= PASO_MAX_VERSIONABLE && archivo && (
                      <button
                        type="button"
                        className="docgen-cancel-btn docgen-version-btn"
                        onClick={realizarCambioVersion}
                        disabled={cambiandoVersion || subiendoArchivo}
                        title="Sube o genera un nuevo documento para este paso, conservando la versión anterior en el histórico de S3."
                      >
                        {cambiandoVersion
                          ? "Preparando nueva versión..."
                          : `↻ Realizar un cambio (crear versión ${
                              ((proyectoActivo?.versionesPorPaso?.[pasoActivo]) || 1) + 1
                            })`}
                      </button>
                    )}

                    {pasoActual.numero !== PASO_SITE_SURVEY && archivo && (
                      <div className="docgen-preview">
                        <div className="docgen-preview-header">
                          <span className="docgen-preview-name">
                            {archivo.name}
                          </span>
                          <span className="docgen-preview-size">
                            {formatearTamanio(archivo.size)}
                          </span>
                        </div>

                        {esImagen && (
                          <img
                            src={previewUrl}
                            alt={`Vista previa de ${archivo.name}`}
                            className="docgen-preview-image"
                          />
                        )}

                        {esPdf && previewUrl && (
                          <div className="docgen-preview-pdf">
                            <PdfViewer key={previewUrl} pdfUrl={previewUrl} />
                          </div>
                        )}

                        {!esImagen && !esPdf && (
                          <div className="docgen-preview-generic">
                            <span className="docgen-preview-generic-icon">
                              📄
                            </span>
                            <p className="docgen-preview-generic-text">
                              No hay vista previa disponible para este tipo
                              de archivo.
                            </p>
                            <a
                              href={previewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="orgproc-card-btn"
                            >
                              Abrir archivo →
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {pasoActual.numero !== PASO_SITE_SURVEY && (
                      <div className="docgen-output">
                        <div className="docgen-output-header">Documento generado</div>
                        <div className="docgen-output-body">
                          <span className="docgen-output-icon">📄</span>
                          <div className="docgen-output-text">
                            <div className="docgen-output-name">{pasoActual.salida}</div>
                            <div className="docgen-output-format">
                              Formato: {pasoActual.numero === PASO_BOM ? "Word" : pasoActual.formatoSalida}
                            </div>
                          </div>
                          <button
                            type="button"
                            className={
                              "orgproc-card-btn" +
                              (pasoActual.numero === PASO_BOM && bomBlob ? "" : " orgproc-card-btn--disabled")
                            }
                            disabled={!(pasoActual.numero === PASO_BOM && bomBlob)}
                            onClick={descargarBOM}
                          >
                            Descargar →
                          </button>
                        </div>
                        {pasoActual.numero === PASO_BOM ? (
                          <p className="docgen-output-hint">
                            {errorBOM
                              ? errorBOM
                              : bomBlob
                              ? "BOM de prueba generado heredando logos y secciones del Site Survey."
                              : "Sube un Site Survey en .docx en el paso 1 y presiona \"Siguiente paso\" para generarlo automáticamente."}
                          </p>
                        ) : (
                          <p className="docgen-output-hint">
                            La generación automática de este documento estará
                            disponible próximamente.
                          </p>
                        )}
                      </div>
                    )}

                    <div className="docgen-step-nav">
                      <button
                        type="button"
                        className="docgen-cancel-btn"
                        onClick={irAPasoAnterior}
                        disabled={pasoActivo === 1 || pasoActivo > PASO_SIN_RETROCESO}
                        title={
                          pasoActivo > PASO_SIN_RETROCESO
                            ? "Ya no se puede regresar después de Agreement."
                            : undefined
                        }
                      >
                        ← Paso anterior
                      </button>
                      <button
                        type="button"
                        className="orgproc-card-btn"
                        onClick={avanzarPaso}
                        disabled={pasoActivo === pasos.length || generandoBOM}
                      >
                        {generandoBOM ? "Procesando..." : "Siguiente paso →"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {vista === "proyecto" && proyectoActivo && (
              <CalendarioProyecto
                proyecto={proyectoActivo}
                pasoActivo={pasoActivo}
                titulosPasos={pasos.map((paso) => paso.titulo)}
              />
            )}
          </div>
        )}
      </Container>

      <RevisionOrtografica
        abierto={revisionAbierta}
        datos={datosSiteSurvey}
        onConfirmar={confirmarRevisionOrtografica}
        onCancelar={cancelarRevisionOrtografica}
      />
    </>
  );
};

export default CreacionDocumentacion;
